import { TextInput, Icon, IconName } from '@mgmresorts/mgm-ui';
import React, { useCallback, useMemo } from 'react';

import { useContent } from '../../../../hooks/useContent';
import { BindInput } from '../../../../hooks/useInput';
import { MaxLengths } from '../../../../utils/constants';

import { StyledPasswordHint, StyledHintContainer, StyledContainer } from './styles';

type PasswordInputProps = BindInput & {
  id: string;
  label: string;
  name: string;
  error?: boolean;
  hint?: string;
  disabled?: boolean;
  className?: string;
  passwordValidations: { [key: string]: boolean };
  testId?: string;
  invalidFields: string[];
};

export function PasswordInput(props: PasswordInputProps) {
  const { value, invalidFields, name, passwordValidations } = props;
  const { getContentByPath } = useContent();

  const isValidPassword = useMemo(
    () => Object.values(passwordValidations).every(validation => validation),
    [passwordValidations],
  );

  const getPasswordFieldError = useCallback(() => {
    if (invalidFields.includes(name)) {
      return getContentByPath('shared.fields.password.empty');
    }

    if (!isValidPassword) {
      return getContentByPath('shared.fields.password.policy');
    }

    if (!invalidFields.includes('password')) {
      return '';
    }

    return '';
  }, [invalidFields, isValidPassword, getContentByPath, name]);

  const error = useMemo(
    () => (invalidFields.includes('password') || (value && !isValidPassword) ? getPasswordFieldError() : undefined),
    [getPasswordFieldError, invalidFields, isValidPassword, value],
  );

  const passwordValidationHint = useCallback(
    (validation: keyof typeof passwordValidations, hint: string) => {
      const isValid = passwordValidations[validation];
      const className = !value ? '' : isValid ? 'hint-success' : 'hint-error';
      const iconName: IconName = !value || isValid ? 'checkmark-circle' : 'symbol-x-circle';

      const requirementText = getContentByPath(
        !value
          ? 'shared.fields.requirement'
          : isValid
          ? 'shared.fields.requirementMet'
          : 'shared.fields.requirementUnmet',
      );

      return (
        <StyledPasswordHint className={className} variant='body-regular-s' as='p'>
          <Icon
            name={iconName}
            size='small'
            role='img'
            aria-hidden={false}
            focusable
            aria-label={requirementText as string}
          />
          {hint}
        </StyledPasswordHint>
      );
    },
    [getContentByPath, passwordValidations, value],
  );

  return (
    <StyledContainer>
      <TextInput variant='password' {...props} maxLength={MaxLengths.Password} />
      <div data-testid='password-hints-wrapper'>
        <StyledHintContainer>
          <>
            {(!value || error) && (
              <>
                {passwordValidationHint('length', getContentByPath('shared.fields.password.lengthHint'))}
                {passwordValidationHint('number', getContentByPath('shared.fields.password.numberHint'))}
                {passwordValidationHint('uppercase', getContentByPath('shared.fields.password.uppercaseHint'))}
                {passwordValidationHint('lowercase', getContentByPath('shared.fields.password.lowercaseHint'))}
                {passwordValidationHint(
                  'specialCharacter',
                  getContentByPath('shared.fields.password.specialCharacterHint'),
                )}
              </>
            )}
            {value && !error && (
              <StyledPasswordHint variant='body-regular-s' className='hint-success'>
                <Icon
                  name='checkmark-circle'
                  size='small'
                  role='img'
                  aria-hidden='false'
                  focusable
                  aria-label={getContentByPath('shared.fields.password.allMet')}
                />
                {getContentByPath('shared.fields.password.allMet')}
              </StyledPasswordHint>
            )}
          </>
        </StyledHintContainer>
      </div>
    </StyledContainer>
  );
}

PasswordInput.displayName = 'PasswordInputField';
